/**
 * <FeatureFlag> - Iter 9.40 Pass D
 *
 * Gates UI based on the user's visible features list, fetched from
 * `/api/me/visible-features`. Backend logic considers tier_visibility[],
 * test_account_user_ids[], and admin status.
 *
 * Usage:
 *   <FeatureFlag feature="brand_pilots">
 *     <BrandPilotsCard />
 *   </FeatureFlag>
 *
 *   <FeatureFlag feature="insights_atlas" fallback={<UpgradePrompt />}>
 *     <InsightsAtlas />
 *   </FeatureFlag>
 *
 *   const { isVisible, loading } = useFeatureFlag("launch_control");
 */
import { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FeatureFlagContext = createContext({
  features: [],
  loading: true,
  error: null,
  isVisible: () => false,
  refetch: () => {},
});

export const FeatureFlagProvider = ({ children }) => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFeatures = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      if (!token) {
        setFeatures([]);
        setLoading(false);
        return;
      }
      const res = await fetch(`${API}/me/visible-features`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`visible-features ${res.status}`);
      const data = await res.json();
      setFeatures(data.features || []);
    } catch (e) {
      setError(e);
      setFeatures([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  const visibleSet = useMemo(
    () => new Set(features.map((f) => f.feature_id)),
    [features],
  );

  const isVisible = useCallback((id) => visibleSet.has(id), [visibleSet]);

  return (
    <FeatureFlagContext.Provider
      value={{ features, loading, error, isVisible, refetch: fetchFeatures }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlag = (featureId) => {
  const { isVisible, loading, error } = useContext(FeatureFlagContext);
  return { isVisible: isVisible(featureId), loading, error };
};

export const useAllFeatures = () => useContext(FeatureFlagContext);

export const FeatureFlag = ({ feature, children, fallback = null, loadingFallback = null }) => {
  const { isVisible, loading } = useFeatureFlag(feature);
  if (loading) return loadingFallback;
  return isVisible ? children : fallback;
};

export default FeatureFlag;
